export type Pillar = Readonly<{
  name: string;
  short: string;
  detail: string;
  examples: readonly string[];
  tone: 'cyan' | 'violet' | 'amber' | 'sky' | 'mint' | 'lavender' | 'navy' | 'rose';
  symbol: string;
  eyebrow?: string;
  dialogTitle?: string;
}>;

export const pillars: readonly Pillar[] = [
  {
    name: 'Physical',
    short: 'Move, recover, hydrate, and sleep.',
    detail: 'Physical wellness is the everyday care that helps your body meet the day you have.',
    examples: ['Movement that fits your day', 'Hydration habits', 'Rest and recovery'],
    tone: 'cyan',
    symbol: '↗',
  },
  {
    name: 'Mental',
    short: 'Make room for focus, calm, and support.',
    detail: 'Mental wellness includes simple tools for stress, focus, connection, and asking for help.',
    examples: ['A two-minute reset', 'Focus between tasks', 'Knowing where support begins'],
    tone: 'violet',
    symbol: '◌',
  },
  {
    name: 'Spiritual',
    short: 'Connect daily choices to meaning.',
    detail: 'Spiritual wellness creates space for meaning, reflection, values, and a sense of belonging.',
    examples: ['A moment to reflect', 'Values in action', 'Connection and gratitude'],
    tone: 'lavender',
    symbol: '✦',
  },
  {
    name: 'Education',
    short: 'Learn something useful in minutes.',
    detail: 'Educational wellness builds confidence through clear lessons people can use in real life.',
    examples: ['Plain-language wellness lessons', 'Benefits education', 'Small skills that travel'],
    tone: 'amber',
    symbol: 'A',
  },
  {
    name: 'Financial',
    short: 'Turn money questions into clearer next steps.',
    detail: 'Financial wellness helps people understand everyday money choices and available resources.',
    examples: ['A simple spending check-in', 'Planning for the unexpected', 'Understanding available help'],
    tone: 'mint',
    symbol: '$',
  },
  {
    name: 'Occupational',
    short: 'Support wellbeing through the workday.',
    detail: 'Occupational wellness looks at sustainable routines, purpose, recovery, and connection at work.',
    examples: ['Reset between demanding tasks', 'Build a safer routine', 'Recognize progress'],
    tone: 'navy',
    symbol: '⌁',
  },
  {
    name: 'Environmental',
    short: 'Shape spaces that make healthier choices easier.',
    detail: 'Environmental wellness considers how the spaces around us affect comfort, access, and routine.',
    examples: ['A calmer workspace', 'Everyday access and safety', 'Small sustainable choices'],
    tone: 'sky',
    symbol: '◎',
  },
  {
    name: 'Challenges',
    short: 'Turn each month into approachable action.',
    detail: 'Monthly challenges bring the wellness dimensions to life through small actions, shared momentum, and practical education.',
    examples: ['A hydration reset', 'Five days of better sleep', 'A financial confidence week'],
    tone: 'rose',
    symbol: '✓',
    eyebrow: 'Put wellness into practice',
    dialogTitle: 'Monthly challenges',
  },
] as const;

export const journeyDetails = {
  discover: {
    title: 'Start with the person',
    eyebrow: '01 · Discover',
    copy: 'The employee chooses where to begin. Wellness Frontier helps turn that choice into a clear, approachable next step.',
    points: ['Employee-controlled starting point', 'Plain-language guidance', 'No employer view into private questions'],
  },
  engage: {
    title: 'Make wellness feel relevant',
    eyebrow: '02 · Engage',
    copy: 'Seven wellness pillars create more ways to participate than a one-size-fits-all activity challenge.',
    points: ['Monthly challenges and education', 'A mix of practical wellness dimensions', 'Progress that is motivating, not clinical'],
  },
  understand: {
    title: 'See program momentum',
    eyebrow: '03 · Understand',
    copy: 'Employers see authorized aggregate participation and program activity, never an employee’s private health story.',
    points: ['Aggregate program reporting', 'Suppression and role rules remain in force', 'No private or personal employee data'],
  },
} as const;

/**
 * The group thread that plays over the support photograph.
 *
 * THESE ARE HUMAN TEXTS BETWEEN COLLEAGUES, NOT APP NOTIFICATIONS, and that is
 * a product constraint rather than a copy preference. `packages/messaging`
 * ships exactly three strings today, all of them about the sign-in code — the
 * product sends no engagement messages at all. A phone showing "you earned 40
 * points" would advertise a capability that does not exist. Two coworkers
 * talking about a rollout needs no capability to be true.
 *
 * The handles are the synthetic ones from `packages/ui/stories/social-copy`.
 * They are not invented for this page and they are certainly not pilot
 * employees: `apps/android/store-assets/listing/README.md` records why the
 * pilot's real people never appear in a public asset, and this is a public
 * asset.
 *
 * English only. The product is bilingual; the marketing site is not
 * (operator decision, 2026-09-05).
 */
export type ThreadMessage = Readonly<{
  from: string;
  text: string;
  /** HR speaking as the organisation, styled as the reply that turns the thread. */
  official?: boolean;
}>;

export const supportThread: readonly ThreadMessage[] = [
  { from: 'Marcus T.', text: 'Do we have another boring step challenge this month?' },
  { from: 'Dee W.', text: 'I hope not' },
  {
    from: 'HR Department',
    text: 'Welcome to Wellness Frontier! No more boring step challenges!',
    official: true,
  },
] as const;

/**
 * Organisations willing to be named publicly as customers.
 *
 * THIS LIST IS EMPTY AND THAT IS THE CORRECT STATE TODAY. The pilot
 * organisation has not agreed to be a public reference (operator, 2026-09-06),
 * and a customer's name and mark are theirs, not ours. Publishing either one
 * before they say yes is a claim we are not entitled to make, and it is the
 * kind of claim that costs the relationship it is trying to advertise.
 *
 * `consent` is required rather than optional on purpose. The failure this
 * guards against is not malice — it is somebody adding a logo in a hurry
 * because the section looked empty and it was obvious who the customer was.
 * Making the record of approval part of the shape means the question "who said
 * yes to this, and when?" has to be answered before the entry compiles, and
 * test/partners.test.mjs fails the build if the answer is left blank.
 *
 * The section renders nothing at all while this is empty. No placeholder
 * boxes, no "your logo here" — an empty logo wall advertises that nobody has
 * agreed, which is worse than not having the section.
 */
export type Partner = Readonly<{
  /** Exactly as the organisation writes it. */
  name: string;
  /** Path under /public/media/partners, supplied by them, used as they supply it. */
  logo: string;
  /** Optional one line about the deployment, only as far as consent extends. */
  note?: string;
  consent: Readonly<{
    /** Named person who approved public use of the name and mark. */
    approvedBy: string;
    /** ISO date of that approval. */
    approvedOn: string;
    /** Where the approval is recorded — email, contract clause, ticket. */
    recordedIn: string;
  }>;
}>;

export const partners: readonly Partner[] = [];
